/* ================================================================
   jaiio-marques.js — annuaire des marques (T082, marques.dc.html)

   <jaiio-brand-directory> filtre à la frappe la liste servie par Liquid,
   répond en une ligne sous le champ (selon le côté : vente ou achat),
   recompte chaque groupe et rend inertes les lettres qui n'ont plus de
   marque. Côté achat, une ligne permet de ne voir que les marques qui ont
   leur collection.

   Sans ce script, la liste reste complète et le rail A–Z reste une suite
   d'ancres : rien ne dépend de lui pour lire ou explorer la page.
   Les textes viennent de Liquid (`<script type="application/json">`).

   <jaiio-brand-check> (plus bas) est le vérificateur de la landing vente.
   ================================================================ */

const normalize = (value) => (value || '')
  .toString()
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

const fill = (template, values) => template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (values[key] ?? ''));

class JaiioBrandDirectory extends HTMLElement {
  connectedCallback() {
    try {
      // Shopify échappe la sortie de `t` (une apostrophe devient &#39;) : les
      // textes sont décodés une fois ici, puisqu'ils seront posés en texte.
      const decoder = document.createElement('textarea');
      const raw = JSON.parse(this.querySelector('[data-mq-texts]').textContent);
      this.texts = Object.fromEntries(Object.entries(raw).map(([key, value]) => {
        decoder.innerHTML = value;
        return [key, decoder.value];
      }));
    } catch (error) {
      return; // sans textes, on garde la liste statique plutôt qu'une réponse vide
    }
    this.isBuyer = this.dataset.side === 'achat';
    this.onlyLinked = false;
    this.input = this.querySelector('[data-mq-input]');
    this.response = this.querySelector('[data-mq-response]');
    this.empty = this.querySelector('[data-mq-empty]');
    this.groups = [...this.querySelectorAll('[data-mq-group]')].map((el) => ({
      el,
      letter: el.dataset.mqGroup,
      count: el.querySelector('[data-mq-count]'),
      items: [...el.querySelectorAll('.mq-it')].map((li) => ({
        li,
        name: li.dataset.name,
        key: normalize(li.dataset.name),
        aliases: (li.dataset.aliases || '').split('|').map(normalize).filter(Boolean),
        note: li.dataset.note || '',
        url: li.dataset.url || '',
        hasCollection: 'collection' in li.dataset,
      })),
    }));
    this.letters = [...this.querySelectorAll('.mq-az [data-mq-letter]')];
    this.all = this.groups.flatMap((g) => g.items);
    if (!this.input) return;

    this.onInput = () => this.update();
    this.input.addEventListener('input', this.onInput);
    this.onClick = (event) => {
      if (!event.target.closest('[data-mq-toggle]')) return;
      this.onlyLinked = !this.onlyLinked;
      // Le bouton est recréé par `respond` : sans ce relais, le focus tombait
      // sur le document après le clic, et le clavier repartait du haut de page.
      this.refocusToggle = true;
      this.update();
    };
    this.response.addEventListener('click', this.onClick);
    this.update();
  }

  disconnectedCallback() {
    if (this.input) this.input.removeEventListener('input', this.onInput);
    if (this.response) this.response.removeEventListener('click', this.onClick);
  }

  matches(item, query) {
    if (this.onlyLinked && !item.hasCollection) return false;
    if (!query) return true;
    return item.key.includes(query) || item.aliases.some((alias) => alias.includes(query));
  }

  update() {
    const raw = this.input.value.trim();
    const query = normalize(raw);
    let shown = 0;

    this.groups.forEach((group) => {
      let visible = 0;
      group.items.forEach((item) => {
        const ok = this.matches(item, query);
        item.li.hidden = !ok;
        if (ok) visible += 1;
      });
      group.el.hidden = visible === 0;
      if (group.count) group.count.textContent = fill(visible > 1 ? this.texts.groupOther : this.texts.groupOne, { count: visible });
      shown += visible;
      // Une lettre sans marque visible ne mène nulle part : elle sort du
      // parcours clavier au lieu de pointer vers un groupe masqué.
      const link = this.letters.find((l) => l.dataset.mqLetter === group.letter);
      if (link) {
        link.classList.toggle('is-off', visible === 0);
        if (visible === 0) { link.setAttribute('tabindex', '-1'); link.setAttribute('aria-disabled', 'true'); }
        else { link.removeAttribute('tabindex'); link.removeAttribute('aria-disabled'); }
      }
    });

    // Côté vente, la réponse sous le champ contient déjà le message d'absence
    // et le contact. Le répéter sous la liste alourdit le même état vide.
    if (this.empty) this.empty.hidden = !this.isBuyer || shown > 0 || !query;
    this.respond(raw, query, shown);
  }

  respond(raw, query, shown) {
    const t = this.texts;
    const exact = query ? this.all.find((item) => item.key === query || item.aliases.includes(query)) : null;
    const line = (strong, rest, action) => {
      const html = document.createElement('span');
      const b = document.createElement('b');
      b.textContent = strong;
      html.append(b, rest);
      this.response.replaceChildren(html);
      if (action) this.response.append(action);
    };
    const link = (label, href, pill) => {
      if (!href) return null;
      const a = document.createElement('a');
      a.href = href;
      a.textContent = label;
      a.className = pill ? 'button button--primary button--small' : 'mq-res__link';
      return a;
    };

    if (!query) {
      if (!this.isBuyer) { this.response.replaceChildren(); return; }
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'mq-res__link';
      toggle.dataset.mqToggle = '';
      toggle.setAttribute('aria-pressed', String(this.onlyLinked));
      toggle.textContent = this.onlyLinked ? fill(t.showAll, { count: this.dataset.total }) : t.onlyLinked;
      const parts = fill(t.linkedCount, { count: this.dataset.linked }).split('|');
      line(parts[0], parts[1] || '', toggle);
      if (this.refocusToggle) {
        this.refocusToggle = false;
        toggle.focus();
      }
      return;
    }

    if (this.isBuyer) {
      if (exact) {
        // Sans collection, l'URL est la recherche filtrée sur la marque (Liquid).
        const parts = fill(exact.hasCollection ? t.brandLinked : t.brandPlain, { brand: exact.name }).split('|');
        line(parts[0], parts[1] || '', link(t.seePieces, exact.url, true));
      } else if (shown) {
        const parts = fill(shown > 1 ? t.partialOther : t.partialOne, { count: shown, query: raw }).split('|');
        line(parts[0], parts[1] || '');
      } else {
        const parts = t.unknown.split('|');
        line(parts[0], parts[1] || '', link(t.createAlert, this.dataset.alertUrl, false));
      }
      return;
    }

    if (exact) {
      const template = exact.note ? t.acceptedNote : t.accepted;
      const parts = fill(template, { brand: exact.name, note: exact.note }).split('|');
      line(parts[0], parts[1] || '', link(t.deposit, this.dataset.depositUrl, true));
    } else if (shown) {
      const parts = fill(shown > 1 ? t.partialOther : t.partialOne, { count: shown, query: raw }).split('|');
      line(parts[0], parts[1] || '');
    } else {
      // Hors liste = non prise (décision du 23/10/2026) : plus de lien « Écrivez-nous ».
      const parts = fill(t.refused, { query: raw }).split('|');
      line(parts[0], parts[1] || '');
    }
  }
}

if (!customElements.get('jaiio-brand-directory')) customElements.define('jaiio-brand-directory', JaiioBrandDirectory);

/* ----------------------------------------------------------------
   <jaiio-brand-check> — vérificateur de marque de la landing vente
   (T061, vendre.dc.html `.mk`) : une réponse en une ligne sous le champ,
   des suggestions quand la saisie est partielle, des marques proposées
   en raccourci. Même source et mêmes textes que l'annuaire côté vente.

   La liste noire passe avant la liste acceptée, comme dans l'ancien
   composant : une marque refusée explicitement l'est même si un alias la
   rapproche d'une marque acceptée.

   Sans ce script, le champ reste un champ : le lien vers l'annuaire et la
   sortie WhatsApp de la section suffisent à répondre.
   ---------------------------------------------------------------- */
const MAX_SUGGESTIONS = 6;

class JaiioBrandCheck extends HTMLElement {
  connectedCallback() {
    try {
      const decoder = document.createElement('textarea');
      const raw = JSON.parse(this.querySelector('[data-bc-texts]').textContent);
      this.texts = Object.fromEntries(Object.entries(raw).map(([key, value]) => {
        decoder.innerHTML = value;
        return [key, decoder.value];
      }));
      const data = JSON.parse(this.querySelector('[data-bc-data]').textContent);
      this.brands = (data.brands || []).map(([name, aliases, note]) => ({
        name,
        note: note || '',
        keys: [normalize(name), ...(aliases || []).map(normalize)].filter(Boolean),
      }));
      this.refused = new Set((data.refused || []).map(normalize).filter(Boolean));
    } catch (error) {
      return; // données illisibles : le champ reste inerte plutôt que de mal répondre
    }
    this.input = this.querySelector('[data-bc-input]');
    this.response = this.querySelector('[data-bc-response]');
    this.suggestions = this.querySelector('[data-bc-suggestions]');
    if (!this.input || !this.response) return;

    this.onInput = () => this.update();
    this.onPick = (event) => {
      const button = event.target.closest('[data-bc-pick]');
      if (!button) return;
      this.input.value = button.dataset.bcPick;
      this.update();
      this.input.focus();
    };
    this.input.addEventListener('input', this.onInput);
    this.addEventListener('click', this.onPick);
    if (this.input.value) this.update();
  }

  disconnectedCallback() {
    if (this.input) this.input.removeEventListener('input', this.onInput);
    this.removeEventListener('click', this.onPick);
  }

  update() {
    const raw = this.input.value.trim();
    const query = normalize(raw);
    this.renderSuggestions([]);
    if (!query) {
      this.say();
      return;
    }

    if (this.refused.has(query)) {
      this.say('is-refused', fill(this.texts.blacklisted, { query: raw }));
      return;
    }

    const exact = this.brands.find((brand) => brand.keys.includes(query));
    if (exact) {
      const template = exact.note ? this.texts.acceptedNote : this.texts.accepted;
      this.say('is-accepted', fill(template, { brand: exact.name, note: exact.note }));
      return;
    }

    const partial = this.brands.filter((brand) => brand.keys.some((key) => key.includes(query)));
    if (partial.length) {
      // Rang 0 : le nom commence par la saisie telle qu'elle est tapée, accents
      // compris (« sé » → Sézane avant Sea) ; rang 1 : il commence par la saisie
      // normalisée ; rang 2 : il la contient, ou un alias la contient.
      const typed = raw.toLocaleLowerCase('fr');
      const rank = (brand) => {
        if (brand.name.toLocaleLowerCase('fr').startsWith(typed)) return 0;
        return brand.keys[0].startsWith(query) ? 1 : 2;
      };
      partial.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name, 'fr'));
      const template = partial.length > 1 ? this.texts.partialOther : this.texts.partialOne;
      this.say('', fill(template, { count: partial.length, query: raw }));
      this.renderSuggestions(partial.slice(0, MAX_SUGGESTIONS));
      return;
    }

    this.say('is-refused', fill(this.texts.refused, { query: raw }));
  }

  // Les textes portent une barre verticale : ce qui la précède est en gras.
  say(state = '', text = '') {
    this.response.className = `bc-res ${state}`.trim();
    if (!text) {
      this.response.replaceChildren();
      return;
    }
    const [strong, rest = ''] = text.split('|');
    const b = document.createElement('b');
    b.textContent = strong;
    this.response.replaceChildren(b, rest);
  }

  renderSuggestions(brands) {
    if (!this.suggestions) return;
    this.suggestions.hidden = brands.length === 0;
    this.suggestions.replaceChildren(...brands.map((brand) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bc-chip';
      button.dataset.bcPick = brand.name;
      button.textContent = brand.name;
      return button;
    }));
  }
}

if (!customElements.get('jaiio-brand-check')) customElements.define('jaiio-brand-check', JaiioBrandCheck);
